import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Save, Trash2, Send, PackageCheck, ClipboardList, X, FileDown, Mail } from "lucide-react";
import { exportPurchaseOrderPdf } from "@/lib/pdf";

const STATUS_TONE = {
  pending: "bg-[#FBF3E4] text-[#8A6A22]",
  draft: "bg-[#F1EDE6] text-[#45423C]", sent: "bg-[#FBF3E4] text-[#8A6A22]",
  partial: "bg-[#EDF2E8] text-[#4A5A3E]", received: "bg-[#EDF2E8] text-[#4A5A3E]",
  closed: "bg-[#F1EDE6] text-[#6F6A60]", cancelled: "bg-[#FEF2F2] text-[#991B1B]",
};

export default function PurchaseOrders() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [edit, setEdit] = useState(null);
  const [receive, setReceive] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["orders", status],
    queryFn: async () => (await api.get(`/procurement/orders?status=${status}`)).data,
  });
  const { data: vendors } = useQuery({ queryKey: ["vendors-lite"], queryFn: async () => (await api.get("/procurement/vendors-lite")).data });

  const save = useMutation({
    mutationFn: async (o) => {
      const body = {
        vendor_id: o.vendor_id, expected_date: o.expected_date || "", notes: o.notes || "",
        lines: (o.lines || []).filter((l) => l.item_id && Number(l.qty) > 0).map((l) => ({
          item_id: l.item_id, name: l.name || "", qty: Number(l.qty) || 0, unit: l.unit || "kg", price: Number(l.price) || 0,
        })),
      };
      return o.id ? (await api.put(`/procurement/orders/${o.id}`, body)).data
        : (await api.post("/procurement/orders", body)).data;
    },
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Η παραγγελία αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const setStatusMut = useMutation({
    mutationFn: async ({ id, st }) => (await api.post(`/procurement/orders/${id}/status?status=${st}`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["orders"] }); toast.success(`Κατάσταση: ${d.status_el}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/procurement/orders/${id}`)).data,
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const doReceive = useMutation({
    mutationFn: async (o) => (await api.post(`/procurement/orders/${o.id}/receive`, {
      lines: o.lines.map((l) => ({ item_id: l.item_id, received_qty: Number(l.received_qty) || 0 })), note: o.note || "",
    })).data,
    onSuccess: (d) => { setReceive(null); qc.invalidateQueries({ queryKey: ["orders"] }); toast.success(`Παραλαβή: ${d.status_el}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const loadSuggest = useMutation({
    mutationFn: async (vendorId) => (await api.get(`/procurement/suggest/${vendorId}`)).data,
    onSuccess: (d) => setEdit((p) => ({ ...p, lines: d.lines })),
    onError: (e) => toast.error(apiErr(e)),
  });

  const canManage = data?.can_manage;
  const hasToSend = (data?.rows || []).some((o) => ["pending", "draft"].includes(o.status));
  const approve = useMutation({
    mutationFn: async (id) => (await api.post(`/procurement/orders/${id}/approve`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Η παραγγελία εγκρίθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const sendOrder = useMutation({
    mutationFn: async (id) => (await api.post(`/procurement/orders/${id}/send`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["orders"] }); toast.success(d.emailed ? "Στάλθηκε email στον προμηθευτή" : "Σημειώθηκε ως σταλμένη (χωρίς email προμηθευτή)"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const cancelOrder = useMutation({
    mutationFn: async (id) => (await api.post(`/procurement/orders/${id}/cancel`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Η παραγγελία ακυρώθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const approveSendAll = useMutation({
    mutationFn: async () => (await api.post("/procurement/orders/approve-send-all")).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["orders"] }); toast.success(`Στάλθηκαν ${d.sent} παραγγελίες (${d.emailed} με email)`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const testEmail = useMutation({
    mutationFn: async (id) => (await api.post(`/procurement/orders/${id}/test-email`)).data,
    onSuccess: (d) => toast.success(d.sent ? `Δοκιμαστικό email στάλθηκε (${d.to})` : "Δεν στάλθηκε email"),
    onError: (e) => toast.error(apiErr(e)),
  });

  const [rfq, setRfq] = useState(null);
  const rfqSearch = useMutation({
    mutationFn: async (q) => (await api.get(`/recipes/ingredients?q=${encodeURIComponent(q)}`)).data,
    onSuccess: (d) => setRfq((p) => (p ? { ...p, results: (d.rows || []).slice(0, 40) } : p)),
  });
  const sendRfq = useMutation({
    mutationFn: async () => (await api.post("/procurement/rfq", {
      vendor_id: rfq.vendor_id, item_ids: Object.keys(rfq.selected || {}), note: rfq.note || "",
    })).data,
    onSuccess: (d) => { setRfq(null); toast.success(d.emailed ? "Το αίτημα προσφοράς στάλθηκε" : "Καταχωρήθηκε (χωρίς email προμηθευτή)"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const pdf = (o) => exportPurchaseOrderPdf(o);

  const total = (lines) => (lines || []).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price) || 0), 0);
  const field = "mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προμήθειες</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Παραγγελίες</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Πρόχειρη → Σταλμένη → Παραλαβή → Κλειστή, με PDF για τον προμηθευτή.</p>
        </div>
        <div className="flex items-end gap-2">
          <select data-testid="po-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#45423C]">
            <option value="all">Όλες</option>
            {Object.entries(data?.statuses || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {canManage && (
            <button data-testid="rfq-new" onClick={() => setRfq({ vendor_id: "", selected: {}, note: "", q: "", results: [] })}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
              <Mail className="h-4 w-4" /> Αίτημα προσφοράς
            </button>
          )}
          {canManage && hasToSend && (
            <button data-testid="po-approve-send-all" onClick={() => { if (window.confirm("Έγκριση & αποστολή όλων των εκκρεμών/πρόχειρων παραγγελιών;")) approveSendAll.mutate(); }} disabled={approveSendAll.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#4A5A3E] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              <Send className="h-4 w-4" /> Έγκριση & αποστολή όλων
            </button>
          )}
          <button data-testid="po-new" onClick={() => setEdit({ vendor_id: "", lines: [], expected_date: "", notes: "" })}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            <Plus className="h-4 w-4" /> Νέα παραγγελία
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">{data?.count || 0} παραγγελίες</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Κωδικός</th>
                <th className="px-3 py-3">Προμηθευτής</th>
                <th className="px-3 py-3">Κατάσταση</th>
                <th className="px-3 py-3 text-center">Γραμμές</th>
                <th className="px-3 py-3 text-right">Σύνολο</th>
                <th className="px-3 py-3 text-right">Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {(data?.rows || []).map((o) => (
                <tr key={o.id} data-testid={`po-row-${o.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold text-[#45423C]">
                    <button data-testid={`po-edit-${o.id}`} onClick={() => setEdit({ ...o })} className="hover:underline">{o.code}</button>
                  </td>
                  <td className="px-3 py-2.5">{o.vendor_name}</td>
                  <td className="px-3 py-2.5">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${STATUS_TONE[o.status] || ""}`}>{o.status_el}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center tabular">{(o.lines || []).length}</td>
                  <td className="px-3 py-2.5 text-right tabular font-semibold">{fmtEuroCents(o.total || 0)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {o.status === "pending" && canManage && (
                      <button data-testid={`po-approve-${o.id}`} onClick={() => approve.mutate(o.id)}
                        className="px-2 text-xs font-semibold text-[#4A5A3E] hover:underline">Έγκριση</button>
                    )}
                    {o.status === "draft" && canManage && (
                      <button data-testid={`po-send-${o.id}`} onClick={() => sendOrder.mutate(o.id)}
                        className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]" title="Αποστολή email στον προμηθευτή"><Send className="h-4 w-4" /></button>
                    )}
                    {["sent", "partial"].includes(o.status) && (
                      <button data-testid={`po-receive-${o.id}`} onClick={() => setReceive({ ...o, lines: (o.lines || []).map((l) => ({ ...l, received_qty: l.received_qty || l.qty })) })}
                        className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]" title="Παραλαβή"><PackageCheck className="h-4 w-4" /></button>
                    )}
                    {["received", "partial"].includes(o.status) && (
                      <button data-testid={`po-close-${o.id}`} onClick={() => setStatusMut.mutate({ id: o.id, st: "closed" })}
                        className="px-2 text-xs font-semibold text-[#C86235] hover:underline">Κλείσιμο</button>
                    )}
                    <button data-testid={`po-pdf-${o.id}`} onClick={() => pdf(o)}
                      className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]" title="PDF"><FileDown className="h-4 w-4" /></button>
                    {canManage && ["pending", "draft", "sent"].includes(o.status) && (
                      <button data-testid={`po-testmail-${o.id}`} onClick={() => testEmail.mutate(o.id)}
                        className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]" title="Δοκιμαστικό email (στον ιδιοκτήτη)"><Mail className="h-4 w-4" /></button>
                    )}
                    {canManage && ["pending", "draft", "sent", "partial"].includes(o.status) && (
                      <button data-testid={`po-cancel-${o.id}`} onClick={() => { if (window.confirm(`Ακύρωση της ${o.code};`)) cancelOrder.mutate(o.id); }}
                        className="px-2 text-xs font-semibold text-[#991B1B] hover:underline">Ακύρωση</button>
                    )}
                  </td>
                </tr>
              ))}
              {!isLoading && (data?.rows || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6F6A60]">Δεν υπάρχουν παραγγελίες.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="po-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">{edit.id ? `Παραγγελία ${edit.code}` : "Νέα παραγγελία"}</h3>
              <button data-testid="po-close-modal" onClick={() => setEdit(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Προμηθευτής</label>
                  <select data-testid="po-vendor" value={edit.vendor_id} onChange={(e) => setEdit({ ...edit, vendor_id: e.target.value })} className={field}>
                    <option value="">— επιλογή —</option>
                    {(vendors?.rows || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Αναμενόμενη παράδοση</label>
                  <input data-testid="po-date" type="date" value={edit.expected_date || ""} onChange={(e) => setEdit({ ...edit, expected_date: e.target.value })} className={field} />
                </div>
                <div className="flex items-end">
                  <button data-testid="po-suggest" disabled={!edit.vendor_id || loadSuggest.isPending} onClick={() => loadSuggest.mutate(edit.vendor_id)}
                    className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold hover:border-[#B9AE9A] disabled:opacity-40">
                    Φόρτωση τιμοκαταλόγου
                  </button>
                </div>
              </div>

              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {(edit.lines || []).map((l, ix) => (
                  <div key={`${l.item_id}-${ix}`} className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-6 text-sm text-[#45423C] truncate" title={l.name}>{l.name || l.item_id}</span>
                    <input data-testid={`po-qty-${ix}`} type="number" value={l.qty} placeholder="0"
                      onChange={(e) => { const lines = [...edit.lines]; lines[ix] = { ...l, qty: e.target.value }; setEdit({ ...edit, lines }); }}
                      className="col-span-2 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                    <span className="col-span-1 text-xs text-[#6F6A60]">{l.unit}</span>
                    <input data-testid={`po-price-${ix}`} type="number" step="0.01" value={l.price}
                      onChange={(e) => { const lines = [...edit.lines]; lines[ix] = { ...l, price: e.target.value }; setEdit({ ...edit, lines }); }}
                      className="col-span-2 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                    <button data-testid={`po-del-line-${ix}`} onClick={() => setEdit({ ...edit, lines: edit.lines.filter((_, i) => i !== ix) })}
                      className="col-span-1 p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {(edit.lines || []).length === 0 && <p className="text-sm text-[#6F6A60]">Διάλεξε προμηθευτή και φόρτωσε τον τιμοκατάλογό του.</p>}
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Σημειώσεις</label>
                <input data-testid="po-notes" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={field} />
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="text-sm text-[#6F6A60]">Σύνολο: <span className="font-semibold text-[#45423C] tabular" data-testid="po-total">{fmtEuroCents(total(edit.lines))}</span></div>
                <div className="flex gap-2">
                  {edit.id && edit.status === "draft" && (
                    <button data-testid="po-delete" onClick={() => { if (window.confirm(`Διαγραφή της ${edit.code};`)) remove.mutate(edit.id); }}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
                      <Trash2 className="h-4 w-4" /> Διαγραφή
                    </button>
                  )}
                  <button data-testid="po-save" onClick={() => save.mutate(edit)} disabled={!edit.vendor_id || save.isPending}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                    <Save className="h-4 w-4" /> Αποθήκευση
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {receive && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="po-receive-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-10 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">Παραλαβή {receive.code} · {receive.vendor_name}</h3>
              <button data-testid="po-receive-close" onClick={() => setReceive(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              {receive.lines.map((l, ix) => (
                <div key={`${l.item_id}-${ix}`} className="grid grid-cols-12 gap-2 items-center">
                  <span className="col-span-6 text-sm truncate" title={l.name}>{l.name}</span>
                  <span className="col-span-2 text-xs text-[#6F6A60] text-right">παρ/θηκαν από {l.qty} {l.unit}</span>
                  <input data-testid={`po-recv-${ix}`} type="number" value={l.received_qty}
                    onChange={(e) => { const lines = [...receive.lines]; lines[ix] = { ...l, received_qty: e.target.value }; setReceive({ ...receive, lines }); }}
                    className="col-span-3 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <button data-testid="po-receive-save" onClick={() => doReceive.mutate(receive)} disabled={doReceive.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                  <PackageCheck className="h-4 w-4" /> Καταχώρηση παραλαβής
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {rfq && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="rfq-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-10 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">Αίτημα προσφοράς σε προμηθευτή</h3>
              <button data-testid="rfq-close" onClick={() => setRfq(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Προμηθευτής</label>
                <select data-testid="rfq-vendor" value={rfq.vendor_id} onChange={(e) => setRfq({ ...rfq, vendor_id: e.target.value })} className={field}>
                  <option value="">— επιλογή —</option>
                  {(vendors?.rows || []).map((v) => <option key={v.id} value={v.id}>{v.name}{v.email ? "" : " (χωρίς email)"}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Προϊόντα — αναζήτησε & πρόσθεσε</label>
                <input data-testid="rfq-search" value={rfq.q || ""} placeholder="Αναζήτηση προϊόντος"
                  onChange={(e) => { const q = e.target.value; setRfq({ ...rfq, q }); if (q.trim().length >= 2) rfqSearch.mutate(q); }}
                  className={field} />
              </div>
              <div className="max-h-[22vh] overflow-y-auto border border-[#F1EDE6] rounded-xl divide-y divide-[#F1EDE6]">
                {(rfq.results || []).map((it) => (
                  <button key={it.id} data-testid={`rfq-add-${it.id}`} onClick={() => setRfq((p) => ({ ...p, selected: { ...p.selected, [it.id]: it.name } }))}
                    className="w-full text-left px-3 py-2 text-sm text-[#45423C] hover:bg-[#FAF8F4] flex justify-between">
                    <span className="truncate">{it.name}</span><span className="text-[#C86235] text-xs">+ προσθήκη</span>
                  </button>
                ))}
                {(rfq.results || []).length === 0 && <p className="px-3 py-3 text-sm text-[#6F6A60]">Γράψε 2+ χαρακτήρες για αναζήτηση.</p>}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="rfq-selected">
                {Object.entries(rfq.selected || {}).map(([id, name]) => (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-[#F1EDE6] px-3 py-1 text-xs text-[#45423C]">
                    {name}
                    <button onClick={() => setRfq((p) => { const s = { ...p.selected }; delete s[id]; return { ...p, selected: s }; })} className="text-[#991B1B]"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {Object.keys(rfq.selected || {}).length === 0 && <span className="text-sm text-[#6F6A60]">Δεν έχεις επιλέξει προϊόντα.</span>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Σημείωση</label>
                <input data-testid="rfq-note" value={rfq.note || ""} onChange={(e) => setRfq({ ...rfq, note: e.target.value })} className={field} />
              </div>
              <div className="flex justify-end">
                <button data-testid="rfq-send" onClick={() => sendRfq.mutate()} disabled={!rfq.vendor_id || Object.keys(rfq.selected || {}).length === 0 || sendRfq.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40"><Mail className="h-4 w-4" /> Αποστολή αιτήματος</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
